import React from "react";

export interface Slice {
  key: string;
  label: string;
  value: number;
  color: string;
}

export const percent = (value: number, total: number) =>
  total ? `${((value / total) * 100).toFixed(1)}%` : "0%";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

// 0 → 1 over `duration` ms, restarted whenever `replayKey` changes, so a
// chart builds up again after a refresh.
const useProgress = (replayKey: string, duration = 1000) => {
  const [progress, setProgress] = React.useState(prefersReducedMotion() ? 1 : 0);

  React.useEffect(() => {
    if (prefersReducedMotion()) {
      setProgress(1);
      return;
    }
    setProgress(0);
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      setProgress(easeOut(t));
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [replayKey, duration]);

  return progress;
};

// Counts up from the previously shown value to the new one.
export const CountUp: React.FC<{ value: number; duration?: number }> = ({
  value,
  duration = 900,
}) => {
  const [shown, setShown] = React.useState(prefersReducedMotion() ? value : 0);
  const shownRef = React.useRef(shown);

  React.useEffect(() => {
    if (prefersReducedMotion()) {
      shownRef.current = value;
      setShown(value);
      return;
    }
    const from = shownRef.current;
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const next = Math.round(from + (value - from) * easeOut(t));
      shownRef.current = next;
      setShown(next);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return <>{shown.toLocaleString()}</>;
};

export const Donut: React.FC<{
  slices: Slice[];
  centerLabel: string;
}> = ({ slices, centerLabel }) => {
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const progress = useProgress(slices.map((s) => `${s.key}:${s.value}`).join("|"));
  const swept = circumference * progress;
  let offset = 0;

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row">
      <svg viewBox="0 0 180 180" className="h-40 w-40 shrink-0">
        <g transform="rotate(-90 90 90)">
          <circle cx="90" cy="90" r={radius} fill="none" stroke="#F2F2F2" strokeWidth="28" />
          {total > 0 &&
            slices.map((slice) => {
              const full = (slice.value / total) * circumference;
              const length = Math.max(0, Math.min(full, swept - offset));
              const dash = (
                <circle
                  key={slice.key}
                  cx="90"
                  cy="90"
                  r={radius}
                  fill="none"
                  stroke={slice.color}
                  strokeWidth="28"
                  strokeDasharray={`${length} ${circumference - length}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += full;
              return dash;
            })}
        </g>
        <text x="90" y="88" textAnchor="middle" className="fill-gray-900 text-[28px] font-bold">
          <CountUp value={total} />
        </text>
        <text x="90" y="110" textAnchor="middle" className="fill-[#747171] text-[11px]">
          {centerLabel}
        </text>
      </svg>
      <ul className="min-w-0 flex-1 space-y-2 text-sm">
        {slices.map((slice) => (
          <li key={slice.key} className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2 text-gray-700">
              <span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: slice.color }} />
              <span className="truncate" title={slice.label}>
                {slice.label}
              </span>
            </span>
            <span className="shrink-0 whitespace-nowrap font-semibold text-gray-900">
              <CountUp value={slice.value} />{" "}
              <span className="font-normal text-[#747171]">({percent(slice.value, total)})</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const BAR_COLORS = ["#AA232F", "#DC2626", "#9D0303", "#F59E0B", "#212121", "#747171"];

export const CountBars: React.FC<{
  rows: { key: string; label: string; count: number }[];
  total?: number;
  emptyText: string;
}> = ({ rows, total, emptyText }) => {
  const sum = total ?? rows.reduce((s, row) => s + row.count, 0);
  const progress = useProgress(rows.map((r) => `${r.key}:${r.count}`).join("|"));
  if (!rows.length) {
    return <p className="py-8 text-center text-sm text-[#747171]">{emptyText}</p>;
  }
  return (
    <ul className="space-y-4">
      {rows.map((row, i) => (
        <li key={row.key}>
          <div className="mb-1 flex justify-between gap-3 text-sm">
            <span className="truncate text-gray-700" title={row.label}>
              {row.label}
            </span>
            <span className="shrink-0 font-semibold text-gray-900">
              <CountUp value={row.count} /> ({percent(row.count, sum)})
            </span>
          </div>
          <div className="h-2 rounded-full bg-[#F2F2F2]">
            <div
              className="h-2 rounded-full"
              style={{
                width: `${sum ? Math.min(100, (row.count / sum) * 100) * progress : 0}%`,
                backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};
